/**
 * @author        calvin@zumper.com (Calvin Yau)
 * @copyright     Copyright (c) 2018, Zumper
 * @description   ManageTitle Component
 */

import { selectIsMounted } from '@zcode-ui/web-zumper-global-state'
import type { Location } from 'history'
import React, { useEffect, useRef } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { DEFAULT_TITLE } from './constants'

function areEqual<TitleProps>(
  prevProps: ManageTitleProps<TitleProps> | undefined,
  nextProps: ManageTitleProps<TitleProps>
) {
  const { makeTitle, title, titleProps } = nextProps
  const switchedFromTitleToMakeTitle = prevProps?.title && !title && makeTitle
  const shouldCheckTitleProps = !title && titleProps && makeTitle
  if (switchedFromTitleToMakeTitle) {
    return false
  }
  if (shouldCheckTitleProps) {
    const makeTitleChanged = prevProps?.makeTitle !== makeTitle
    const titlePropsChanged = !shallowEqual(prevProps?.titleProps, titleProps)
    const anythingChanged = makeTitleChanged || titlePropsChanged
    return !anythingChanged
  }
  return prevProps?.title === title
}

let initialLocation: Location<unknown>
const isStillInitialLocation = (location: Location<unknown>) => {
  return location === initialLocation
}

interface ManageTitleTitleProps {
  makeTitle?: never
  title: string
  titleProps?: never
}

interface ManageTitleMakeTitleProps<TitleProps> {
  makeTitle: (titleProps: TitleProps) => string | null | undefined
  title?: never
  titleProps: TitleProps
}

type ManageTitleProps<TitleProps> = ManageTitleTitleProps | ManageTitleMakeTitleProps<TitleProps>

export function ManageTitle<TitleProps>(props: ManageTitleProps<TitleProps>) {
  const isAppMounted = useSelector(selectIsMounted)
  const location = useLocation()
  if (!initialLocation) {
    initialLocation = location
  }
  const prevProps = useRef<ManageTitleProps<TitleProps>>()
  const prevTitle = useRef<string>()
  useEffect(() => {
    try {
      if (isStillInitialLocation(location) || !isAppMounted) {
        return
      }
      if (prevProps && areEqual(prevProps.current, props)) {
        return
      }
      const { makeTitle, title, titleProps } = props
      const newTitle = title ?? (makeTitle(titleProps) || DEFAULT_TITLE)
      if (typeof window !== 'undefined' && window.document && window.document.title !== newTitle) {
        window.document.title = newTitle
      }
      prevTitle.current = newTitle
    } finally {
      prevProps.current = props
    }
  }, [isAppMounted, location, props])

  return null
}
